/**
 * Campaign Table Component
 * Reusable table component for campaign tabs with customizable styling
 */

import { type FC, memo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CampaignTable.module.css';

type CampaignTableType = 'winners' | 'prizes' | 'winnings' | 'default';

interface CampaignTableColumn {
  name: string;
  key: string;
  className?: string;
  cellClassName?: string;
}

interface CampaignTableProps {
  children: ReactNode;
  className?: string;
  headClassName?: string;
  bodyClassName?: string;
  columns?: CampaignTableColumn[];
  tableType?: CampaignTableType;
}

const CampaignTable: FC<CampaignTableProps> = ({ 
  children, 
  className, 
  headClassName,
  bodyClassName,
  columns,
  tableType = 'default'
}) => {
  const { t } = useTranslation();
  const tableClass = className ? `${styles.table} ${className}` : styles.table;
  const headClass = headClassName ? `${styles.tableHead} ${headClassName}` : styles.tableHead;
  const bodyClass = bodyClassName ? `${styles.tableBody} ${bodyClassName}` : styles.tableBody;
  
  return (
    <table className={tableClass} data-table-type={tableType}>
      <thead className={headClass}>
        {columns && (
          <tr className={`${styles.tableRow} ${styles.tableHeaderRow}`} data-table-type={tableType}>
            {columns.map((column) => (
              <th 
                key={column.name}
                className={`${styles.tableHeaderCell} ${column.className || ''}`}
                data-table-type={tableType}
              >
                <span className={column.cellClassName}>{t(column.key, column.name)}</span>
              </th>
            ))}
          </tr>
        )}
      </thead>
      <tbody className={bodyClass}>
        {children}
      </tbody>
    </table>
  );
};

export default memo(CampaignTable);
